import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import Pagination from "../../DesignSystem/Pagination/Pagination";
import OutlinedStatusButton from "../../DesignSystem/Buttons/OutlinedStatusButtons";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import {
  loadCommissionSplitAdvisers,
  subscribeCommissionSplitEvents,
} from "./commissionSplitEventsService";
import CommissionSplitBulkUpload from "./CommissionSplitBulkUpload";

const STATUSES = ["created", "skipped", "failed"];
const DASHBOARD_TABS = [...STATUSES, "bulk-upload"];
const STATUS_COLOURS = { created: "green", skipped: "yellow", failed: "red" };
const EVENTS_PER_PAGE = 10;
const VIEW_STATE_KEY = "commissionSplitEventsViewState";
const CURRENT_MONTH = dayjs().format("YYYY-MM");

const getSavedViewState = () => {
  try {
    const savedState = JSON.parse(sessionStorage.getItem(VIEW_STATE_KEY));
    const activeTab = DASHBOARD_TABS.includes(savedState?.activeTab)
      ? savedState.activeTab
      : "created";
    const currentPage = Number(savedState?.currentPage);

    return {
      activeTab,
      currentPage:
        Number.isInteger(currentPage) && currentPage > 0 ? currentPage : 1,
      searchTerm: String(savedState?.searchTerm || ""),
      selectedMonth: String(savedState?.selectedMonth || CURRENT_MONTH),
    };
  } catch {
    return {
      activeTab: "created",
      currentPage: 1,
      searchTerm: "",
      selectedMonth: CURRENT_MONTH,
    };
  }
};

const capitaliseFirst = (value) => {
  const text = String(value || "").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

const formatReadableStatus = (value) =>
  capitaliseFirst(
    String(value || "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " "),
  );

const normaliseStatus = (event) => {
  const status = String(event.status || "")
    .trim()
    .toLowerCase();
  if (STATUSES.includes(status)) return status;

  const splitStatus = String(event.splitStatus || "").toLowerCase();
  if (event.apiError || splitStatus.includes("fail")) return "failed";
  if (splitStatus.includes("skip") || splitStatus.includes("no splits"))
    return "skipped";
  return "created";
};

const formatTimestamp = (value) => {
  const rawValue = typeof value?.toDate === "function" ? value.toDate() : value;
  const parsedValue = dayjs(rawValue);
  return parsedValue.isValid()
    ? parsedValue.format("DD MMM YYYY, HH:mm:ss")
    : "-";
};

const getTimestampValue = (value) => {
  const rawValue = typeof value?.toDate === "function" ? value.toDate() : value;
  const timestamp = dayjs(rawValue).valueOf();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const getEventMonth = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const rawValue = typeof value?.toDate === "function" ? value.toDate() : value;
  const parsedValue = dayjs(rawValue);
  return parsedValue.isValid() ? parsedValue.format("YYYY-MM") : "";
};

const normaliseEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const formatClients = (clients) => {
  if (!Array.isArray(clients) || !clients.length) return "-";
  return (
    clients
      .map((client) => client?.name || client?.id)
      .filter(Boolean)
      .join(", ") || "-"
  );
};

const formatPlanId = (value) => {
  const planId = String(value || "").trim();
  if (!planId) return "";
  return /^IOB/i.test(planId) ? planId : `IOB${planId}`;
};

const CommissionSplitEventsDashboard = () => {
  const [savedViewState] = useState(getSavedViewState);
  const [events, setEvents] = useState([]);
  const [advisersByEmail, setAdvisersByEmail] = useState(new Map());
  const [advisersLoading, setAdvisersLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(savedViewState.activeTab);
  const [searchTerm, setSearchTerm] = useState(savedViewState.searchTerm);
  const [selectedMonth, setSelectedMonth] = useState(
    savedViewState.selectedMonth,
  );
  const [currentPage, setCurrentPage] = useState(savedViewState.currentPage);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState("");
  const { userPermissions = [] } = useUserPermissions();
  const canOpenAdviserRates = userPermissions.some((permission) =>
    ["finance admin", "administrator", "admin"].includes(
      String(permission || "")
        .trim()
        .toLowerCase(),
    ),
  );
  const canBulkUpload = userPermissions.some((permission) =>
    ["finance", "finance admin", "administrator", "admin"].includes(
      String(permission || "")
        .trim()
        .toLowerCase(),
    ),
  );

  useEffect(
    () =>
      subscribeCommissionSplitEvents(
        (items) => {
          setEvents(items);
          setLoading(false);
        },
        () => {
          setError("We couldn't load the commission split events.");
          setLoading(false);
        },
      ),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    loadCommissionSplitAdvisers()
      .then((advisers) => {
        if (cancelled) return;
        setAdvisersByEmail(
          new Map(
            advisers
              .filter((adviser) => adviser.email)
              .map((adviser) => [normaliseEmail(adviser.email), adviser]),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setAdvisersByEmail(new Map());
      })
      .finally(() => {
        if (!cancelled) setAdvisersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const eventsWithStatus = useMemo(
    () =>
      events.map((event) => ({
        ...event,
        dashboardStatus: normaliseStatus(event),
      })),
    [events],
  );

  const monthOptions = useMemo(() => {
    const months = new Set([
      CURRENT_MONTH,
      selectedMonth,
      ...eventsWithStatus.map((event) => getEventMonth(event.timestamp)),
    ]);

    return [...months]
      .filter(Boolean)
      .sort((left, right) => right.localeCompare(left))
      .map((month) => ({
        id: month,
        name: dayjs(`${month}-01`).format("MMMM YYYY"),
      }));
  }, [eventsWithStatus, selectedMonth]);

  const monthFilteredEvents = useMemo(
    () =>
      eventsWithStatus.filter(
        (event) => getEventMonth(event.timestamp) === selectedMonth,
      ),
    [eventsWithStatus, selectedMonth],
  );

  const searchFilteredEvents = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return monthFilteredEvents.filter(
      (event) =>
        !query ||
        [
          event.planId,
          formatPlanId(event.planId),
          event.adviserEmail,
          event.adviserName,
          advisersByEmail.get(normaliseEmail(event.adviserEmail))?.name,
          ...(Array.isArray(event.clientDetails)
            ? event.clientDetails.flatMap((client) => [
                client?.name,
                client?.id,
              ])
            : []),
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
        ),
    );
  }, [advisersByEmail, monthFilteredEvents, searchTerm]);

  const tabs = [
    ...STATUSES.map((status) => ({
      id: status,
      label: status.charAt(0).toUpperCase() + status.slice(1),
      count: searchFilteredEvents.filter(
        (event) => event.dashboardStatus === status,
      ).length,
    })),
    { id: "bulk-upload", label: "Bulk Upload" },
  ];

  const visibleEvents = useMemo(() => {
    return searchFilteredEvents
      .filter((event) => event.dashboardStatus === activeTab)
      .sort(
        (left, right) =>
          getTimestampValue(right.timestamp) -
          getTimestampValue(left.timestamp),
      );
  }, [activeTab, searchFilteredEvents]);

  const totalPages = Math.max(
    1,
    Math.ceil(visibleEvents.length / EVENTS_PER_PAGE),
  );
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * EVENTS_PER_PAGE;
    return visibleEvents.slice(startIndex, startIndex + EVENTS_PER_PAGE);
  }, [currentPage, visibleEvents]);

  useEffect(() => {
    if (!loading && !advisersLoading) {
      setCurrentPage((page) => Math.min(page, totalPages));
    }
  }, [advisersLoading, loading, totalPages]);

  useEffect(() => {
    sessionStorage.setItem(
      VIEW_STATE_KEY,
      JSON.stringify({ activeTab, currentPage, searchTerm, selectedMonth }),
    );
  }, [activeTab, currentPage, searchTerm, selectedMonth]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="Main-Page bg-lavender-web">
      <Helmet>
        <title>Commission Split Events</title>
      </Helmet>
      <Sidebar />
      <main className="Main-Container table-contained-page table-scroll-page column gap-s">
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            { label: "Commission Split Events" },
          ]}
        />

        <header className="row space-between gap-l">
          <div>
            <div className="FP_H3">Commission Split Events</div>
            <p className="FP_Help_Text primary">
              Monitor commission split webhook results from Intelliflo.
            </p>
          </div>
          {activeTab !== "bulk-upload" && <div className="row gap-s">
            <SearchField
              id="commission-split-search"
              label=""
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Plan ID, adviser or client"
            />
            <CustomSelect
              options={monthOptions}
              value={selectedMonth}
              onChange={handleMonthChange}
              placeholder="Select month"
              clearable={false}
              searchable={false}
            />
            {!loading && visibleEvents.length > EVENTS_PER_PAGE && (
              <div className="row-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>}
        </header>

        {/* {error && (
          <div className="FP_Help_Text text-error" role="alert">
            {error}
          </div>
        )} */}

        {/* {SHOW_DEMO_DATA && (
          <div className="FP_Help_Text primary" role="status">
            Showing development demo data. No records have been written to
            Firebase.
          </div>
        )} */}

        <section className="bg-white card-corner-radius table-scroll-card">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          {activeTab === "bulk-upload" ? (
            <CommissionSplitBulkUpload canUpload={canBulkUpload} />
          ) : (
          <div className="table-scroll-container table-rows-scroll-container">
            <table className="commission-split-striped-table">
              <thead>
                <tr>
                  {/* <th>Event details</th> */}
                  {/* <th></th> */}
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    adviser, client &amp; PLAN details
                  </th>
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    Split outcome
                  </th>
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="3" className="FP_Help_Text primary">
                      Loading commission split events...
                    </td>
                  </tr>
                ) : paginatedEvents.length ? (
                  paginatedEvents.map((event, index) => (
                    <tr key={event.id}>
                      {/* <th className="FP_Help_Text_Semibold primary"></th> */}
                      <td>
                        <div className="row gap-s">
                          <span className="FP_Help_Text primary-text">
                            {" "}
                            {(currentPage - 1) * EVENTS_PER_PAGE + index + 1}
                            .{" "}
                          </span>
                          <div className="fit-column gap-s">
                            {event.adviserEmail &&
                              (() => {
                                const adviser = advisersByEmail.get(
                                  normaliseEmail(event.adviserEmail),
                                );
                                const adviserName =
                                  adviser?.name ||
                                  event.adviserName ||
                                  event.adviserEmail;
                                return (
                                  <div className="row gap-s">
                                    <span className="FP_Help_Text primary">
                                      Adviser
                                    </span>
                                    <strong className="FP_Help_Text_Semibold primary">
                                      {canOpenAdviserRates ? (
                                        <Link
                                          className="table-url-link primary-text"
                                          to={`/advisers/${encodeURIComponent(
                                            adviser?.id || event.adviserEmail,
                                          )}?tab=rates`}
                                        >
                                          {adviserName} |{" "}
                                          {event.sellingAdviserId}
                                        </Link>
                                      ) : (
                                        <>
                                          {adviserName} |{" "}
                                          {event.sellingAdviserId}
                                        </>
                                      )}
                                    </strong>
                                  </div>
                                );
                              })()}
                            {formatClients(event.clientDetails) !== "-" && (
                              <div className="row gap-s">
                                <span className="FP_Help_Text primary">
                                  Client
                                </span>
                                <strong className="FP_Help_Text_Semibold">
                                  {formatClients(event.clientDetails)}
                                </strong>
                              </div>
                            )}
                            {event.planId && (
                              <div className="row gap-s">
                                <span className="FP_Help_Text primary">
                                  Plan ID
                                </span>
                                <strong className="FP_Help_Text_Semibold">
                                  {formatPlanId(event.planId)} |{" "}
                                  {formatReadableStatus(event.planStatus)}
                                  {/* {event.id} */}
                                </strong>
                              </div>
                            )}
                            {/* {event.planStatus && (
                            <div className="row gap-s">
                              <span className="FP_Help_Text primary">
                                Plan status
                              </span>
                              <strong className="FP_Help_Text_Semibold">
                                {formatReadableStatus(event.planStatus)}
                              </strong>
                            </div>
                          )} */}

                            {/* {event.sellingAdviserId && (
                            <div className="row gap-s">
                              <span className="FP_Help_Text primary">
                                Selling adviser ID
                              </span>
                              <strong className="FP_Help_Text_Semibold">
                                {event.sellingAdviserId}
                              </strong>
                            </div>
                          )} */}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="fit-column gap-s">
                          {event.timestamp && (
                            <div className="row gap-s">
                              <span className="FP_Help_Text primary">
                                Triggered
                              </span>
                              <strong className="FP_Help_Text_Semibold">
                                {formatTimestamp(event.timestamp)}
                              </strong>
                            </div>
                          )}
                          {event.splitStatus && (
                            <div className="row gap-s">
                              <span className="FP_Help_Text primary">
                                Split result
                              </span>
                              <strong className="FP_Help_Text_Semibold">
                                {formatReadableStatus(event.splitStatus)}
                              </strong>
                            </div>
                          )}
                          {event.apiError && (
                            <div className="row gap-s">
                              <span className="FP_Help_Text primary">
                                API error
                              </span>
                              <strong className="FP_Help_Text_Semibold text-error">
                                {event.apiError}
                              </strong>
                            </div>
                          )}
                        </div>
                      </td>

                      <td>
                        <OutlinedStatusButton
                          label={capitaliseFirst(event.dashboardStatus)}
                          color={STATUS_COLOURS[event.dashboardStatus]}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="FP_Help_Text primary">
                      No {activeTab} commission split events found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default CommissionSplitEventsDashboard;
